import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SaveJobButton } from "@/components/SaveJobButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Building2, DollarSign, Globe, Layers, Clock, Mail, ExternalLink, Loader2, ArrowLeft, CheckCircle2, Star, Sparkles } from "lucide-react";

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [cover, setCover] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [membership, setMembership] = useState(false);
  const [applied, setApplied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [profile, setProfile] = useState(null);
  const [aiCoverLoading, setAiCoverLoading] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${jobId}`).then(({ data }) => setJob(data)).catch(() => toast.error("Job not found")).finally(() => setLoading(false));
  }, [jobId]);

  useEffect(() => {
    if (user?.role === "applicant") {
      api.get("/applicant/profile").then(({ data }) => { setMembership(data.membership_active); setProfile(data.profile); }).catch(() => {});
      api.get("/applicant/applications").then(({ data }) => setApplied(data.some((a) => a.job_id === jobId))).catch(() => {});
      api.get("/saved-jobs").then(({ data }) => setSaved(data.some((j) => j.job_id === jobId))).catch(() => {});
    }
  }, [user, jobId]);

  const startApply = () => {
    if (!user) return navigate("/login", { state: { redirect: `/jobs/${jobId}` } });
    if (user.role !== "applicant") return toast.error("Only applicant accounts can apply.");
    if (!membership) return navigate("/membership");
    setApplyOpen(true);
  };

  const submitApply = async () => {    setSubmitting(true);
    try {
      const { data } = await api.post("/applications", { job_id: jobId, cover_letter_text: cover });
      toast.success(data.email_status === "sent" ? "Application sent to employer!" : "Application submitted (email retry pending).");
      setApplied(true); setApplyOpen(false);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      if (e.response?.status === 400 && msg.includes("profile")) { toast.error("Complete your profile first."); navigate("/applicant"); }
      else if (e.response?.status === 402) { toast.error("Membership required."); navigate("/membership"); }
      else toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const genCover = async () => {
    const bg = [profile?.certifications, profile?.cover_letter_text, cover].filter(Boolean).join(". ");
    if (!bg.trim()) return toast.error("Add certifications to your profile or a short note first");
    setAiCoverLoading(true);
    try {
      const { data } = await api.post("/ai/cover-letter", { job_title: job.job_title, company: job.company_name, background: bg });
      setCover(data.text);
      toast.success("Cover letter drafted — edit as you like");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed"); }
    finally { setAiCoverLoading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;
  if (!job) return <div className="min-h-screen flex flex-col"><Header /><div className="flex-1 flex items-center justify-center text-slate-500">Job not found.</div><Footer /></div>;

  const salary = job.salary_not_disclosed ? "Not disclosed" : job.salary_range || "Not disclosed";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-8 w-full flex-1">
        <button onClick={() => navigate("/jobs")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy-800 mb-6" data-testid="back-to-jobs"><ArrowLeft className="h-4 w-4" /> Back to jobs</button>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              {job.featured && <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-amber-600 mb-2" data-testid="detail-featured-badge"><Star className="h-3 w-3" fill="currentColor" /> Featured listing</div>}
              <h1 className="font-display text-3xl font-extrabold text-navy-800">{job.job_title}</h1>
              <div className="flex items-center gap-1.5 text-slate-500 mt-2"><Building2 className="h-4 w-4" /> {job.company_name}</div>
            </div>
            {typeof job.days_left === "number" && <Badge variant="outline" className="border-amber-300 text-amber-700 gap-1"><Clock className="h-3 w-3" /> {job.days_left} days left</Badge>}
            {user?.role === "applicant" && <SaveJobButton jobId={job.job_id} saved={saved} />}
          </div>

          <div className="flex flex-wrap gap-2 mt-5">
            <Badge variant="secondary" className="bg-slate-100 text-slate-700 gap-1"><MapPin className="h-3 w-3" /> {job.remote ? "Remote" : `${job.city}, ${job.state}`}</Badge>
            {job.remote && !job.multi_state && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1"><Globe className="h-3 w-3" /> Remote</Badge>}
            {job.multi_state && <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 gap-1"><Layers className="h-3 w-3" /> Multi-State</Badge>}
            <Badge variant="outline" className="border-slate-300 text-slate-600">{job.employment_type}</Badge>
            <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 gap-1"><DollarSign className="h-3 w-3" /> {salary}</Badge>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            <Button onClick={startApply} disabled={applied} data-testid="apply-button" className="bg-amber-500 hover:bg-amber-600 text-navy-900 font-bold sm:col-span-1 h-11">
              {applied ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Applied</> : "Apply now"}
            </Button>
            {job.company_website && <a href={job.company_website.startsWith("http") ? job.company_website : `https://${job.company_website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 h-11 rounded-lg border border-slate-300 text-sm font-semibold text-navy-800 hover:bg-slate-50">Company site <ExternalLink className="h-3.5 w-3.5" /></a>}
          </div>
          {!membership && user?.role === "applicant" && !applied && <p className="text-xs text-amber-700 mt-3 flex items-center gap-1"><Mail className="h-3 w-3" /> A $10 / 30-day membership is required to apply.</p>}

          <div className="mt-8 space-y-8">
            <Section title="Job description" body={job.description} />
            <Section title="Minimum qualifications" body={job.min_qualifications} />
          </div>
        </div>
      </div>

      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent data-testid="apply-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Apply to {job.job_title}</DialogTitle>
            <DialogDescription>Your saved profile, resume and cover letter will be sent to {job.company_name}.</DialogDescription>
          </DialogHeader>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-navy-800">Message to employer (optional)</label>
              <Button type="button" variant="outline" size="sm" onClick={genCover} disabled={aiCoverLoading} data-testid="cover-ai-generate" className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50">{aiCoverLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Draft with AI</Button>
            </div>
            <Textarea data-testid="apply-cover-input" value={cover} onChange={(e) => setCover(e.target.value)} placeholder="Add a short note…" className="mt-2" rows={6} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-apply-button" onClick={submitApply} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-navy-900 font-bold">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

const Section = ({ title, body }) => (
  <div>
    <h3 className="font-display text-lg font-bold text-navy-800 mb-2">{title}</h3>
    <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{body}</p>
  </div>
);
